import React, { useState, useEffect } from 'react';
import { Video, Clock, FileText, Stethoscope, Lock, ArrowRight, CheckCircle, Shield, Heart, Phone } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const VideoConsultations = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const features = [
    {
      icon: Clock,
      title: 'Instant Connection',
      description: 'Connect with specialists in under 60 seconds. No waiting rooms, no delays.',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      iconColor: '#667eea'
    },
    {
      icon: FileText,
      title: 'Seamless Prescriptions',
      description: 'Get digital prescriptions instantly after consultation. Direct pharmacy delivery.',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      iconColor: '#f093fb'
    },
    {
      icon: Stethoscope,
      title: 'Top 1% Doctors',
      description: 'Board-certified specialists with 10+ years experience. Verified credentials.',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      iconColor: '#4facfe'
    },
    {
      icon: Lock,
      title: '100% Private',
      description: 'End-to-end encrypted video calls. HIPAA-compliant secure consultations.',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      iconColor: '#43e97b'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Choose Doctor',
      description: 'Browse specialists by category, ratings, and availability. Pick your preferred doctor.',
      icon: Stethoscope,
      color: '#1D57D5'
    },
    {
      step: '02',
      title: 'Video Chat',
      description: 'Start instant HD video consultation. Share symptoms, medical history, and get diagnosed.',
      icon: Video,
      color: '#F4CE7B'
    },
    {
      step: '03',
      title: 'Get Prescription',
      description: 'Receive digital prescription, lab orders, and follow-up care plan instantly.',
      icon: FileText,
      color: '#28C08A'
    }
  ];

  return (
    <>
      <Navbar />
      
      {/* Hero Section - Split Screen */}
      <section style={{
        background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 50%, #1a4bb8 100%)',
        minHeight: isMobile ? 'auto' : '90vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: isMobile ? '100px 16px 60px' : '80px 40px',
        marginTop: '40px',
      }}>
        {/* Background Decorations - Hidden on mobile */}
        {!isMobile && (
          <>
            <div style={{
              position: 'absolute',
              top: '10%',
              left: '5%',
              width: '80px',
              height: '80px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              animation: 'float 6s ease-in-out infinite'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '15%',
              right: '8%',
              width: '60px',
              height: '60px',
              background: 'rgba(244, 206, 123, 0.2)',
              borderRadius: '50%',
              animation: 'float 8s ease-in-out infinite 1s'
            }} />
          </>
        )}

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: isMobile ? '0' : '0 40px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '40px' : '80px',
            alignItems: 'center',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            {/* Left Side - Content */}
            <div style={{ zIndex: 10, flex: 1, width: '100%' }}>
              {/* Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(244, 206, 123, 0.15)',
                border: '2px solid rgba(244, 206, 123, 0.3)',
                borderRadius: '50px',
                padding: isSmallMobile ? '8px 16px' : '10px 20px',
                marginBottom: isSmallMobile ? '24px' : '32px',
                backdropFilter: 'blur(10px)'
              }}>
                <Video size={isSmallMobile ? 16 : 20} color="#F4CE7B" />
                <span style={{
                  color: '#F4CE7B',
                  fontSize: isSmallMobile ? '12px' : '14px',
                  fontWeight: '600',
                  letterSpacing: '0.5px'
                }}>LIVE NOW</span>
              </div>

              {/* Main Headline */}
              <h1 style={{
                fontSize: isSmallMobile ? '32px' : isMobile ? '40px' : '64px',
                fontWeight: '900',
                color: 'white',
                lineHeight: '1.1',
                marginBottom: isSmallMobile ? '16px' : '24px',
                letterSpacing: '-1px'
              }}>
                Instant Video Consultations.{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Zero Waiting.
                </span>
              </h1>

              {/* Subheadline */}
              <p style={{
                fontSize: isSmallMobile ? '16px' : isMobile ? '18px' : '20px',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.6',
                marginBottom: isSmallMobile ? '28px' : '40px',
                maxWidth: isMobile ? '100%' : '520px',
                margin: isMobile ? '0 auto 28px' : '0 0 40px 0'
              }}>
                Connect with <strong>top specialists</strong> in under 60 seconds. 
                Get diagnosed, treated, and prescribed—all from home.
              </p>

              {/* CTA Button */}
              <button style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
                color: '#133E97',
                fontSize: isSmallMobile ? '16px' : '18px',
                fontWeight: '700',
                padding: isSmallMobile ? '16px 32px' : '20px 48px',
                borderRadius: '50px',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                boxShadow: '0 8px 32px rgba(244, 206, 123, 0.4), 0 16px 64px rgba(244, 206, 123, 0.2)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: 'translateY(0)',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '320px' : 'none',
                minHeight: '52px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 48px rgba(244, 206, 123, 0.5), 0 24px 80px rgba(244, 206, 123, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(244, 206, 123, 0.4), 0 16px 64px rgba(244, 206, 123, 0.2)';
              }}>
                <Video size={isSmallMobile ? 18 : 22} />
                Start Video Call Now
                <ArrowRight size={isSmallMobile ? 16 : 20} />
              </button>

              {/* Trust Indicators */}
              <div style={{
                marginTop: isSmallMobile ? '32px' : '48px',
                display: 'flex',
                flexDirection: isSmallMobile ? 'column' : 'row',
                gap: isSmallMobile ? '16px' : '32px',
                alignItems: 'center',
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle size={isSmallMobile ? 16 : 20} color="#43e97b" />
                  <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: isSmallMobile ? '13px' : '14px' }}>
                    10,000+ Consultations
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Shield size={isSmallMobile ? 16 : 20} color="#F4CE7B" />
                  <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: isSmallMobile ? '13px' : '14px' }}>
                    100% Secure
                  </span>
                </div>
              </div>
            </div>

            {/* Right Side - 3D Phone Illustration - Hidden on small mobile, scaled on tablet */}
            {!isSmallMobile && (
            <div style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10,
              flex: isMobile ? 'none' : 1,
              width: isMobile ? '100%' : 'auto',
              maxWidth: isMobile ? '280px' : 'none',
              margin: isMobile ? '0 auto' : '0'
            }}>
              {/* Phone Mockup */}
              <div style={{
                position: 'relative',
                width: isMobile ? '260px' : '340px',
                height: isMobile ? '520px' : '680px',
                background: 'linear-gradient(135deg, #1A1A1A 0%, #2C2C2C 100%)',
                borderRadius: isMobile ? '36px' : '48px',
                padding: isMobile ? '10px' : '12px',
                boxShadow: '0 30px 80px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                animation: 'float 6s ease-in-out infinite'
              }}>
                {/* Notch */}
                <div style={{
                  position: 'absolute',
                  top: isMobile ? '10px' : '12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: isMobile ? '100px' : '140px',
                  height: isMobile ? '22px' : '28px',
                  background: '#000',
                  borderRadius: '0 0 20px 20px',
                  zIndex: 2
                }} />

                {/* Screen */}
                <div style={{
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(180deg, #E3F2FD 0%, #FFFFFF 100%)',
                  borderRadius: '40px',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {/* Doctor on Video Call SVG */}
                  <svg viewBox="0 0 316 656" fill="none" style={{ width: '100%', height: '100%' }}>
                    {/* Background */}
                    <rect width="316" height="656" fill="url(#videoGradient)" />
                    
                    {/* Video Call Interface */}
                    <rect x="20" y="60" width="276" height="400" rx="20" fill="white" />
                    
                    {/* Doctor Avatar Circle */}
                    <circle cx="158" cy="200" r="80" fill="url(#doctorGradient)" />
                    
                    {/* Doctor Face - Simple Illustration */}
                    <circle cx="158" cy="185" r="50" fill="#FFE4D6" />
                    {/* Eyes */}
                    <circle cx="145" cy="180" r="4" fill="#333" />
                    <circle cx="171" cy="180" r="4" fill="#333" />
                    {/* Smile */}
                    <path d="M 145 195 Q 158 205 171 195" stroke="#333" strokeWidth="3" fill="none" strokeLinecap="round" />
                    {/* Stethoscope around neck */}
                    <path d="M 130 220 Q 158 230 186 220" stroke="#1D57D5" strokeWidth="4" fill="none" />
                    <circle cx="130" cy="220" r="5" fill="#1D57D5" />
                    <circle cx="186" cy="220" r="5" fill="#1D57D5" />
                    
                    {/* Doctor Name Badge */}
                    <rect x="80" y="280" width="156" height="50" rx="10" fill="#F0F7FF" />
                    <text x="158" y="300" textAnchor="middle" fill="#1D57D5" fontSize="14" fontWeight="600">
                      Dr. Sarah Mitchell
                    </text>
                    <text x="158" y="318" textAnchor="middle" fill="#64748B" fontSize="12">
                      Cardiologist • 12y exp
                    </text>
                    
                    {/* Status Indicator */}
                    <circle cx="250" cy="90" r="8" fill="#43e97b">
                      <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <text x="230" y="95" fill="#43e97b" fontSize="11" fontWeight="600">LIVE</text>
                    
                    {/* Call Controls at Bottom */}
                    <g transform="translate(0, 480)">
                      {/* Control Bar */}
                      <rect x="40" y="0" width="236" height="60" rx="30" fill="rgba(29, 87, 213, 0.1)" />
                      
                      {/* Mute Button */}
                      <circle cx="88" cy="30" r="20" fill="white" stroke="#1D57D5" strokeWidth="2" />
                      <path d="M 83 25 L 83 35 C 83 37 85 37 87 37 L 89 37 C 91 37 93 37 93 35 L 93 25 C 93 23 91 23 89 23 L 87 23 C 85 23 83 23 83 25" fill="#1D57D5" />
                      
                      {/* End Call Button */}
                      <circle cx="158" cy="30" r="24" fill="#FF4B55" />
                      <rect x="145" y="26" width="26" height="8" rx="4" fill="white" transform="rotate(-45 158 30)" />
                      
                      {/* Video Button */}
                      <circle cx="228" cy="30" r="20" fill="white" stroke="#1D57D5" strokeWidth="2" />
                      <rect x="220" y="24" width="16" height="12" rx="2" fill="#1D57D5" />
                      <path d="M 236 27 L 242 24 L 242 36 L 236 33 Z" fill="#1D57D5" />
                    </g>
                    
                    {/* Bottom Prescription Notification */}
                    <g transform="translate(0, 570)">
                      <rect x="30" y="0" width="256" height="60" rx="15" fill="white" stroke="#F4CE7B" strokeWidth="2" />
                      <circle cx="55" cy="30" r="15" fill="#FFF9E6" />
                      <path d="M 50 30 L 52 32 L 60 24" stroke="#F4CE7B" strokeWidth="2" fill="none" strokeLinecap="round" />
                      <text x="78" y="27" fill="#333" fontSize="12" fontWeight="600">Prescription Ready</text>
                      <text x="78" y="42" fill="#64748B" fontSize="10">View & Download</text>
                    </g>
                    
                    <defs>
                      <linearGradient id="videoGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#E3F2FD" />
                        <stop offset="100%" stopColor="#FFFFFF" />
                      </linearGradient>
                      <linearGradient id="doctorGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1D57D5" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#1D57D5" stopOpacity="0.1" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                {/* Home Indicator */}
                <div style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: isMobile ? '100px' : '140px',
                  height: '5px',
                  background: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '10px'
                }} />
              </div>

              {/* Floating Medical Icons - Hidden on mobile */}
              {!isMobile && (
                <>
                  {/* Shield Icon */}
                  <div style={{
                    position: 'absolute',
                    top: '10%',
                    right: '10%',
                    width: '64px',
                    height: '64px',
                    background: 'rgba(244, 206, 123, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(244, 206, 123, 0.3)',
                    animation: 'float 5s ease-in-out infinite 0.5s'
                  }}>
                    <Shield size={32} color="#F4CE7B" />
                  </div>

                  {/* Heart Icon */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-5%',
                    width: '64px',
                    height: '64px',
                    background: 'rgba(255, 75, 85, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(255, 75, 85, 0.3)',
                    animation: 'float 6s ease-in-out infinite 1s'
                  }}>
                    <Heart size={32} color="#FF4B55" fill="#FF4B55" />
                  </div>

                  {/* Cross/Plus Icon */}
                  <div style={{
                    position: 'absolute',
                    bottom: '15%',
                    right: '5%',
                    width: '64px',
                    height: '64px',
                    background: 'rgba(67, 226, 123, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(67, 226, 123, 0.3)',
                    animation: 'float 7s ease-in-out infinite 1.5s',
                    fontSize: '40px',
                    fontWeight: '600',
                    color: '#43e97b'
                  }}>
                    +
                  </div>
                </>
              )}
            </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Grid - Glassmorphism Cards */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '120px 40px',
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', textAlign: isMobile ? 'center' : 'left' }}>
          {/* Section Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)',
            border: '2px solid #1D57D5',
            borderRadius: '50px',
            padding: isSmallMobile ? '8px 16px' : '10px 24px',
            marginBottom: isSmallMobile ? '20px' : '24px'
          }}>
            <CheckCircle size={isSmallMobile ? 14 : 18} color="#1D57D5" />
            <span style={{
              color: '#1D57D5',
              fontSize: isSmallMobile ? '11px' : '14px',
              fontWeight: '700',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>Why Choose Video Consultations</span>
          </div>

          <h2 style={{
            fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
            fontWeight: '800',
            color: '#1A202C',
            marginBottom: '16px',
            letterSpacing: '-0.5px',
            lineHeight: '1.2'
          }}>
            Healthcare at Your{' '}
            <span style={{
              background: 'linear-gradient(135deg, #1D57D5 0%, #4facfe 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Fingertips
            </span>
          </h2>

          <p style={{
            fontSize: isSmallMobile ? '15px' : '18px',
            color: '#64748B',
            marginBottom: isSmallMobile ? '40px' : '64px',
            maxWidth: isMobile ? '100%' : '600px',
            lineHeight: '1.6',
            margin: isMobile ? '0 auto 40px' : '0 0 64px 0'
          }}>
            Experience the future of healthcare with instant video consultations. 
            Quality care without compromise.
          </p>

          {/* Feature Cards Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: isSmallMobile ? '20px' : isMobile ? '20px' : '32px'
          }}>
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: isSmallMobile ? '20px' : '24px',
                    padding: isSmallMobile ? '28px 20px' : isMobile ? '32px 24px' : '40px 32px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(29, 87, 213, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                    e.currentTarget.style.borderColor = feature.iconColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                  }}
                >
                  {/* Gradient Background */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '6px',
                    background: feature.gradient,
                    opacity: 0.8
                  }} />

                  {/* Icon */}
                  <div style={{
                    width: isSmallMobile ? '52px' : '64px',
                    height: isSmallMobile ? '52px' : '64px',
                    borderRadius: isSmallMobile ? '14px' : '16px',
                    background: feature.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: isSmallMobile ? '16px' : '24px',
                    boxShadow: `0 8px 24px ${feature.iconColor}40`
                  }}>
                    <Icon size={isSmallMobile ? 24 : 32} color="white" />
                  </div>

                  {/* Content */}
                  <h3 style={{
                    fontSize: isSmallMobile ? '18px' : '22px',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: isSmallMobile ? '8px' : '12px'
                  }}>
                    {feature.title}
                  </h3>

                  <p style={{
                    fontSize: isSmallMobile ? '14px' : '15px',
                    color: '#64748B',
                    lineHeight: '1.7'
                  }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section - How It Works */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '120px 40px',
        background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(29, 87, 213, 0.05) 1px, transparent 0)',
          backgroundSize: '40px 40px',
          opacity: 0.5
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '48px' : '80px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #FFF9E6 0%, #FFEDD5 100%)',
              border: '2px solid #F4CE7B',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '20px' : '24px'
            }}>
              <ArrowRight size={isSmallMobile ? 14 : 18} color="#D4A84E" />
              <span style={{
                color: '#D4A84E',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>Simple Process</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-0.5px',
              lineHeight: '1.2'
            }}>
              How It Works
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: '#64748B',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Three simple steps to connect with top doctors and get the care you need
            </p>
          </div>

          {/* Process Steps */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isSmallMobile ? '40px' : isMobile ? '48px' : '48px',
            position: 'relative',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Connection Lines - Hidden on mobile */}
            {!isMobile && (
              <div style={{
                position: 'absolute',
                top: '80px',
                left: '25%',
                right: '25%',
                height: '2px',
                background: 'linear-gradient(90deg, #1D57D5 0%, #F4CE7B 50%, #28C08A 100%)',
                opacity: 0.3,
                zIndex: 0
              }} />
            )}

            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={index}
                  style={{
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                    flex: isMobile ? 'none' : '1',
                    width: isMobile ? '100%' : 'auto',
                    maxWidth: isMobile ? '320px' : 'none'
                  }}
                >
                  {/* Step Number */}
                  <div style={{
                    width: isSmallMobile ? '40px' : '48px',
                    height: isSmallMobile ? '40px' : '48px',
                    borderRadius: '50%',
                    background: `${step.color}20`,
                    border: `3px solid ${step.color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    fontSize: isSmallMobile ? '14px' : '18px',
                    fontWeight: '700',
                    color: step.color
                  }}>
                    {step.step}
                  </div>

                  {/* Icon Circle */}
                  <div style={{
                    width: isSmallMobile ? '90px' : isMobile ? '100px' : '120px',
                    height: isSmallMobile ? '90px' : isMobile ? '100px' : '120px',
                    borderRadius: '50%',
                    background: 'white',
                    boxShadow: `0 12px 40px ${step.color}30`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: `3px solid ${step.color}20`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
                    e.currentTarget.style.boxShadow = `0 20px 60px ${step.color}50`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                    e.currentTarget.style.boxShadow = `0 12px 40px ${step.color}30`;
                  }}>
                    <div style={{
                      width: isSmallMobile ? '60px' : isMobile ? '70px' : '80px',
                      height: isSmallMobile ? '60px' : isMobile ? '70px' : '80px',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}CC 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon size={isSmallMobile ? 28 : isMobile ? 32 : 40} color="white" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 style={{
                    fontSize: isSmallMobile ? '20px' : '24px',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: '12px'
                  }}>
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p style={{
                    fontSize: isSmallMobile ? '14px' : '15px',
                    color: '#64748B',
                    lineHeight: '1.7',
                    maxWidth: '280px',
                    margin: '0 auto'
                  }}>
                    {step.description}
                  </p>

                  {/* Arrow for non-last items - Hidden on mobile */}
                  {!isMobile && index < processSteps.length - 1 && (
                    <div style={{
                      position: 'absolute',
                      top: '80px',
                      right: '-24px',
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2
                    }}>
                      <ArrowRight size={28} color={step.color} style={{ opacity: 0.5 }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* CTA at bottom */}
          <div style={{
            textAlign: 'center',
            marginTop: isSmallMobile ? '48px' : '80px'
          }}>
            <button style={{
              background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 100%)',
              color: 'white',
              fontSize: isSmallMobile ? '16px' : '18px',
              fontWeight: '700',
              padding: isSmallMobile ? '16px 32px' : '20px 48px',
              borderRadius: '50px',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              boxShadow: '0 8px 32px rgba(29, 87, 213, 0.4)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              width: isMobile ? '100%' : 'auto',
              maxWidth: isMobile ? '320px' : 'none',
              minHeight: '52px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 12px 48px rgba(29, 87, 213, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(29, 87, 213, 0.4)';
            }}>
              Get Started Now
              <ArrowRight size={isSmallMobile ? 18 : 20} />
            </button>

            <p style={{
              marginTop: '16px',
              fontSize: isSmallMobile ? '13px' : '14px',
              color: '#64748B',
              lineHeight: '1.6'
            }}>
              Available 24/7 • No appointment needed • Instant connection
            </p>
          </div>
        </div>
      </section>

      <Footer />

      {/* Add floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </>
  );
};

export default VideoConsultations;
